import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import SyntaxHighlighter from "@/components/ui/syntax-highlighter.tsx";
import "./StreamingCodeComponent.scss";
import { CommonService } from "@/services";
import commonService from "@/services/common.service.ts";
import { IRootReducerState } from "@/store/reducers";
import AppPreviewAndCodeStatusComponent from "@/components/common-components/app-preview-and-code-status/AppPreviewAndCodeStatusComponent.tsx";
import { useAppSelector } from "@/store/hooks.ts"; // Added useAppDispatch
import { AppType } from "@/models/project.model.ts";

// IMPORT New Editor and utils
import FuturisticCodeEditor from '@/components/futuristic-editor/FuturisticCodeEditor';
import { buildFileTree, extractFileContents, InputFiles, FileSystemEntry as EditorFileSystemEntry } from '@/components/futuristic-editor/file-utils.ts';
import { setTemporaryChanges, setDetectedRoutes } from "@/store/actions/projectdata.action.ts"; // For updating Redux
import { useDebouncedCallback } from "use-debounce";
import {useDispatch} from "react-redux";
import { detectRoutes, generateRouteSuggestions } from "@/utils/route-detection.ts";

interface StreamingFileContent {
    [key: string]: string;
}

const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.15, delayChildren: 0.1 },
    },
};

const fileVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            type: "spring",
            stiffness: 100,
            damping: 12,
        },
    },
    exit: {
        opacity: 0,
        y: -50,
        transition: { duration: 0.4, ease: "easeInOut" },
    },
};

const contentVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.5, ease: "easeOut" },
    },
};


const StreamingComponent = () => {
    const [displayedFiles, setDisplayedFiles] = useState<StreamingFileContent>({}); // For streaming animation
    const [activeStreamingFile, setActiveStreamingFile] = useState<string | null>(null); // For streaming animation
    const containerRef = useRef<HTMLDivElement>(null);
    const currentFilePath = useRef<string | null>(null); // For streaming animation path
    const isStreamingRef = useRef<boolean>(false);

    // REMOVE: const [files, setFiles] = useState<SandpackFiles>({});
    // REMOVE: const [sandpackKey, setSandpackKey] = useState<number>(0);
    const [isStreamingInProgress, setIsStreamingInProgress] = useState<boolean>(false);
    const appType: AppType = useMemo(() => commonService.getAppType(location.pathname), []);

    const codeFromStore = useAppSelector((state: IRootReducerState) => state.projectData.tempCode || state.projectData.code); // Use tempCode if available, fallback to code
    const currentProjectDetails = useAppSelector((state: IRootReducerState) => state.projectData.currentProjectDetails);
    const dispatch = useDispatch();

    // State for FuturisticCodeEditor
    const [editorFileTree, setEditorFileTree] = useState<EditorFileSystemEntry[]>([]);
    const [editorFileContents, setEditorFileContents] = useState<Record<string, string>>({});

    useEffect(() => {
        try {
            if (!codeFromStore) {
                setEditorFileTree([]);
                setEditorFileContents({});
                return;
            }
            const parsedCode: InputFiles = JSON.parse(codeFromStore);

            // Filter out environment files (as was done for Sandpack)
            // const filteredCode: InputFiles = Object.fromEntries(
            //     Object.entries(parsedCode).filter(([file]) => !file.includes(".env.") && !file.includes("/.env."))
            // );
            const filteredCode: InputFiles = parsedCode;

            setEditorFileTree(buildFileTree(filteredCode));
            setEditorFileContents(extractFileContents(filteredCode));

            // Detect routes for Next.js applications
            if (appType === 'nextjs') {
                const routeDetectionResult = detectRoutes(filteredCode);
                const routeSuggestions = generateRouteSuggestions(routeDetectionResult.routes);
                
               
                
                // Store detected routes in Redux
                dispatch(setDetectedRoutes({
                    routes: routeDetectionResult.routes,
                    suggestions: routeSuggestions,
                    hasAppRouter: routeDetectionResult.hasAppRouter,
                    hasPagesRouter: routeDetectionResult.hasPagesRouter,
                    appDirectory: routeDetectionResult.appDirectory,
                    pagesDirectory: routeDetectionResult.pagesDirectory
                }));
            }

        } catch (error) {
            console.error("Failed to parse code for editor:", error);
            setEditorFileTree([]);
            setEditorFileContents({});
        }
    }, [codeFromStore, currentProjectDetails?.supabaseEnvs, appType, dispatch]); // currentProjectDetails?.supabaseEnvs was in original Sandpack deps

    // Debounced handler for dispatching changes to Redux
    const debouncedUpdateRedux = useDebouncedCallback((filePath: string, newContent: string) => {
        try {
            const currentFiles: InputFiles = codeFromStore ? JSON.parse(codeFromStore) : {};
            // Update the content of the specific file
            // Ensure paths are consistent (e.g. leading slashes) - Sandpack paths were like "src/App.js"
            // The buildFileTree and extractFileContents should handle paths consistently.
            // The filePath from FuturisticCodeEditor should match keys in currentFiles.

            // Create a new object with the updated file content
            const updatedFiles: InputFiles = {
                ...currentFiles,
                [filePath]: { code: newContent } // Assuming SandpackFiles format { [path]: { code: string } }
            };

            // If original was just string, update as string. For now, assume object with code property.
            // This needs to align with how `codeFromStore` is structured. If it can be Record<string, string>
            // or Record<string, {code: string}>, this logic needs to be robust.
            // The original FileExplorerComponent did: fileChangesRef.current[fileName] = { active: true, code: newValue };
            // This implies the store expects an object with a code property.

            dispatch(setTemporaryChanges(JSON.stringify(updatedFiles)));
        } catch (e) {
            console.error("Error updating Redux with temporary changes:", e);
        }
    }, 300); // 300ms debounce

    const handleEditorFileContentChange = (filePath: string, newContent: string) => {
        // Update local state for FuturisticCodeEditor's consumption if it were managing its own full content map
        // However, FuturisticCodeEditor gets initialFileContents and reports changes.
        // So, we primarily need to inform Redux.
        setEditorFileContents(prevContents => ({
            ...prevContents,
            [filePath]: newContent,
        }));
        debouncedUpdateRedux(filePath, newContent);
    };


    useEffect(() => {
        const startSub = CommonService._communications.startCodeStreamerSubject.subscribe(setIsStreamingInProgress);
        const endSub = CommonService._communications.endCodeStreamerSubject.subscribe(() => setIsStreamingInProgress(false));
        return () => { startSub.unsubscribe(); endSub.unsubscribe(); };
    }, []);

    // REMOVE: sandpackProps memo

    const scrollToBottom = useCallback(() => { /* ... (keep existing) ... */ }, []);
    const processChunk = useCallback( /* ... (keep existing, ensure setActiveStreamingFile and setDisplayedFiles are used) ... */
        (chunk: string) => {
            try {
                const message = commonService.processStream(chunk);
                const eventType = message?.type;
                const eventData = message?.data;
                const path = eventData?.path;
                switch (eventType) {
                    case "file_start":
                        if (path) {
                            currentFilePath.current = path;
                            setDisplayedFiles((prev) => {
                                if(Object.keys(prev).length === 0 && appType !== 'html' ){
                                    CommonService._communications.switchCodeAndPreviewSubject.next(true);
                                }
                                return { ...prev, [path]: "" };
                            });
                            setActiveStreamingFile(path); // Use dedicated state for streaming animation
                        }
                        break;
                    case "file_delta": {
                        const delta = eventData?.delta || "";
                        if (path && delta !== undefined) {
                            setDisplayedFiles((prev) => {
                                const currentContent = (prev[path] || "") + delta;
                                const lines = currentContent.split('\n');
                                if (lines?.length > 30) {
                                    return { ...prev, [path]: lines?.slice(-30).join('\n') };
                                }
                                return { ...prev, [path]: currentContent };
                            });
                            scrollToBottom();
                        }
                        break;
                    }
                    case "file_end":
                        currentFilePath.current = null;
                        break;
                    default:
                        console.warn("Unknown event:", eventType);
                }
            } catch (err) {
                console.error("Error processing chunk:", chunk, err);
            }
        },
        [scrollToBottom, appType]
    );


    useEffect(() => { /* ... (keep existing for processChunk) ... */
        const sub = CommonService._communications.handleCodeStreamerSubject.subscribe(processChunk);
        return () => sub.unsubscribe();
    }, [processChunk]);

    useEffect(() => { /* ... (keep existing for isStreamingRef and startCodeStreamerSubject) ... */
        const sub = CommonService._communications.startCodeStreamerSubject.subscribe((value: boolean) => {
            if (value) { isStreamingRef.current = true; }
        });
        return () => sub.unsubscribe();
    }, []);

    useEffect(() => { /* ... (keep existing for isStreamingRef and endCodeStreamerSubject, ensure setActiveStreamingFile(null)) ... */
        const sub = CommonService._communications.endCodeStreamerSubject.subscribe((value: boolean) => {
            if (value) {
                isStreamingRef.current = false;
                currentFilePath.current = null;
                setActiveStreamingFile(null); // Clear active file for streaming animation
                setTimeout(() => { setDisplayedFiles({}) }, 200);
            }
        });
        return () => sub.unsubscribe();
    }, []);
    

    return (
        <div className="streaming-code-editor-wrapper"> {/* Added a wrapper class */}
            {isStreamingRef.current && (activeStreamingFile || (activeStreamingFile && displayedFiles[activeStreamingFile])) ? (
                <motion.div
                    className="streaming-container"
                    ref={containerRef}
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                >
                    <AnimatePresence mode="popLayout">
                        {/* Render based on activeStreamingFile and displayedFiles */}
                        {activeStreamingFile && displayedFiles[activeStreamingFile] && (
                             <motion.div
                                key={activeStreamingFile} // Keyed by activeStreamingFile
                                layout
                                variants={fileVariants}
                                initial="hidden"
                                animate="visible"
                                exit="exit"
                                className="code-block"
                            >
                                <div className="file-header">{activeStreamingFile}</div>
                                <motion.div variants={contentVariants} className="code-content">
                                    <SyntaxHighlighter
                                        code={displayedFiles[activeStreamingFile!]}
                                        language={commonService.getLanguageFromFilename(activeStreamingFile || "txt") || "plaintext"}
                                    />
                                </motion.div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </motion.div>
            ) : (
                <>
                    {!(codeFromStore && codeFromStore !== '{}' && Object.keys(editorFileContents).length > 0) && !isStreamingInProgress ? (
                        <AppPreviewAndCodeStatusComponent
                            type="info"
                            title="Apps Built by AI. Ideas Powered by You."
                            description="Build full web apps just by describing them. Our AI handles the code so you can focus on what matters—your ideas."
                        />
                    ) : (
                        <FuturisticCodeEditor
                            initialFileTree={editorFileTree}
                            initialFileContents={editorFileContents}
                            onFileContentChange={handleEditorFileContentChange}
                            projectId={currentProjectDetails?.id}
                            // Pass currentAppConfiguration if FuturisticCodeEditor needs it for templates/options
                            // currentAppConfiguration={currentAppConfiguration}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default StreamingComponent;
